import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { fmtDate, initials } from "@/lib/format";
import { StatusBadge, PageHeader, EmptyState, ErrorState } from "@/components/shared";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Phone, Mail, ListChecks, Check, PhoneCall } from "lucide-react";
import { isPast, isToday, parseISO } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function dueMeta(dateStr) {
  if (!dateStr) return { label: "", cls: "" };
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return { label: "Today", cls: "text-warning" };
    if (isPast(d)) return { label: "Overdue", cls: "text-error" };
    return { label: "Upcoming", cls: "text-muted-foreground" };
  } catch {
    return { label: "", cls: "" };
  }
}

export default function FollowUps() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["followups"], queryFn: async () => (await client.get("/followups")).data });
  const rows = data || [];

  const complete = async (c, e) => {
    e.stopPropagation();
    try {
      await client.put(`/customers/${c.id}`, { follow_up_date: "", status: "won" });
      toast.success(`${c.name} marked as won`);
      qc.invalidateQueries({ queryKey: ["followups"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch {
      toast.error("Could not update");
    }
  };

  const logCall = async (c, e) => {
    e.stopPropagation();
    try {
      await client.post(`/customers/${c.id}/interactions`, { type: "call", note: "Follow-up call from list" });
      toast.success(`Call logged for ${c.name}`);
      qc.invalidateQueries({ queryKey: ["interactions", c.id] });
    } catch {
      toast.error("Could not log call");
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Follow-ups" subtitle="Leads that need a call or email next" testid="followups-header" />

      {isError ? (
        <ErrorState onRetry={refetch} message="We couldn't load your follow-ups. Please try again." />
      ) : isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/50" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ListChecks} title="All caught up!" hint="No follow-ups scheduled. Set a next-action date on a lead to see it here." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
          {rows.map((c) => {
            const due = dueMeta(c.follow_up_date);
            return (
              <div key={c.id} onClick={() => navigate(`/customers/${c.id}`)} data-testid={`followup-row-${c.id}`}
                className="flex cursor-pointer items-center gap-4 border-b border-border p-4 last:border-0 transition-colors hover:bg-muted/40">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(c.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-ink">{c.name}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs">
                    <span className={cn("font-semibold", due.cls)}>{due.label}</span>
                    <span className="text-muted-foreground">· {fmtDate(c.follow_up_date)}</span>
                    {c.company && <span className="hidden truncate text-muted-foreground sm:inline">· {c.company}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} data-testid={`call-${c.id}`} className="grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-primary" title="Call">
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} data-testid={`email-${c.id}`} className="grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-primary" title="Email">
                      <Mail className="h-4 w-4" />
                    </a>
                  )}
                  <button onClick={(e) => logCall(c, e)} data-testid={`logcall-${c.id}`} className="hidden h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-primary sm:grid" title="Log a call">
                    <PhoneCall className="h-4 w-4" />
                  </button>
                  <Button size="sm" variant="outline" onClick={(e) => complete(c, e)} data-testid={`complete-${c.id}`} className="gap-1.5 text-success hover:text-success">
                    <Check className="h-4 w-4" /><span className="hidden sm:inline">Won</span>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
